import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { IonItemSliding } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { WeeklyPlanning } from "src/model/class/general classes/weekly-planning";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iConnToolbarColour } from "src/components/shared-components/interfaces/iConnToolbarColour";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AlertService } from "src/service/alert.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { SystemRouterService } from "src/service/system-router.service";
import { ToastService } from "src/service/toast.service";
import { ToastType } from "src/components/shared-components/enum/enumMisc";
import { WeeklyPlanningService } from "src/service/weekly-planning.service";

@Component({
    selector: "app-weekly-planning-item",
    templateUrl: "./weekly-planning-item.component.html",
    styleUrls: ["./weekly-planning-item.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

/**
 * Single weekly-planning row rendered by the weekly-plannings list.
 *
 * Mirrors the schedule row: the thumbnail toggles the planning active state, the swipe
 * actions open the editor (where the time-bands are edited) and delete the planning.
 */
export class WeeklyPlanningItemComponent
  implements OnInit, OnDestroy, iTranslation, iConnToolbarColour
{
  private _logContext: string = "Weekly-planning-item.component";

  private _translateSubscription: Subscription = null;
  private _networkServiceSubscription: Subscription = null;

  private _lblDelete: string = "";
  private _lblDeleteMessage: string = "";
  private _lblUpdate: string = "";
  private _lblUpdateActivate: string = "";
  private _lblUpdateDeactivate: string = "";
  private _lblNotViewable: string = "";
  private _lblNotReady: string = "";

  public toolBarColor: string = "primary";
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public isOnline: boolean = false;

  /** The weekly-planning rendered by this row, provided by the parent list. */
  @Input("WeeklyPlanning") currentWeeklyPlanning: WeeklyPlanning;
  @Input("allowEdit") allowEdit: boolean = true;
  @Input("allowDelete") allowDelete: boolean = true;

  constructor(
    private _alertService: AlertService,
    private _weeklyPlanningService: WeeklyPlanningService,
    private _translateService: TranslateService,
    private _networkService: NetworkService,
    private _cd: ChangeDetectorRef,
    private _loggerService: LoggerService,
    private _systemRouterService: SystemRouterService,
    private _toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();
  }

  ngOnDestroy() {
    if (this._translateSubscription) {
      this._translateSubscription.unsubscribe();
    }
    if (this._networkServiceSubscription) {
      this._networkServiceSubscription.unsubscribe();
    }
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.deleteWeeklyPlanning",
        "dictionary.deleteWeeklyPlanningMessage",
        "dictionary.updateWeeklyPlanning",
        "dictionary.updateWeeklyPlanningActivate",
        "dictionary.updateWeeklyPlanningDeactivate",
        "dictionary.weeklyPlanningNotViewableExplain",
        "dictionary.weeklyPlanningNotReadyExplain",
      ])
      .subscribe((translation) => {
        this._lblDelete = translation["dictionary.deleteWeeklyPlanning"];
        this._lblDeleteMessage =
          translation["dictionary.deleteWeeklyPlanningMessage"];
        this._lblUpdate = translation["dictionary.updateWeeklyPlanning"];
        this._lblUpdateActivate =
          translation["dictionary.updateWeeklyPlanningActivate"];
        this._lblUpdateDeactivate =
          translation["dictionary.updateWeeklyPlanningDeactivate"];
        this._lblNotViewable =
          translation["dictionary.weeklyPlanningNotViewableExplain"];
        this._lblNotReady =
          translation["dictionary.weeklyPlanningNotReadyExplain"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Whether the time-bands view has anything to show for this planning.
   *
   * Mirrors the gate on the editor's header shortcut, so the two never disagree: a planning with
   * no address or no T value paints a mute full-day slice with a blank legend.
   *
   * @returns true when the time-bands view is worth opening
   */
  public get isViewable(): boolean {
    return WeeklyPlanning.isViewable(this.currentWeeklyPlanning);
  }

  /**
   * Open the editor for the current planning. Editing the time-bands happens there, so this
   * action is available to every role (residents included).
   *
   * @param event the originating DOM event
   * @param item the sliding item to close after navigating
   */
  public onEditWeeklyPlanning(event: any, item: IonItemSliding): void {
    this._systemRouterService.navigateToWeeklyPlanningEditData(
      this.currentWeeklyPlanning.id
    );
    item?.close();
  }

  /**
   * Jump straight to the time-bands of the current planning, skipping the editor's registry form.
   *
   * That form used to be the only way there (list, editor, then its header shortcut), which made
   * the one thing a resident may actually change the furthest away. Editing the bands stays gated
   * by `canEditWeeklyPlanningDailySector` inside the view itself.
   *
   * When the planning carries no bands yet the action explains why rather than opening an empty
   * chart. Which explanation depends on who is asking: naming the missing address and T value
   * tells whoever may go and set them what to do, but to a resident — who holds neither the
   * permission nor the vocabulary — "address" reads as the street they live on. They get told to
   * ask the administrator instead, {@link allowEdit} standing in for "may configure this".
   *
   * @param event the originating DOM event
   * @param item the sliding item to close afterwards
   */
  public async onOpenDailySectors(
    event: any,
    item: IonItemSliding
  ): Promise<void> {
    if (!this.isViewable) {
      await this._toastService.showToast(
        ToastType.INFO,
        this.allowEdit ? this._lblNotViewable : this._lblNotReady,
        3000
      );
      item?.close();
      return;
    }

    this._systemRouterService.navigateToWeeklyPlanningTestSingleDay(
      this.currentWeeklyPlanning.id
    );
    item?.close();
  }

  /**
   * Ask for confirmation and delete the current planning.
   *
   * @param event the originating DOM event
   * @param item the sliding item to close afterwards
   */
  public async onDeleteWeeklyPlanning(
    event: any,
    item: IonItemSliding
  ): Promise<void> {
    event.stopPropagation();

    try {
      const allowDelete: boolean =
        await this._alertService.presentAlertConfirmOrDismiss(
          this._lblDelete,
          "",
          this._lblDeleteMessage
        );

      if (allowDelete) {
        await this._weeklyPlanningService.DeleteweeklyPlanning(
          this.currentWeeklyPlanning.id
        );
      }
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "onDeleteWeeklyPlanning()",
        "ERROR: " + error
      );
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      item?.close();
    }
  }

  /**
   * Ask for confirmation and toggle the planning active state. Gated by {@link allowEdit}:
   * residents may only edit the time-bands, not activate/deactivate the whole planning.
   */
  public async onActivateDeactivateWeeklyPlanning(): Promise<void> {
    if (!this.allowEdit) {
      return;
    }

    /* Toggling reaches the gateway: offline the request can only fail, and asking for
     * confirmation just to answer "something went wrong" misdescribes what happened. */
    if (!this.isOnline) {
      await this._alertService.presentAlertCommandNotAvaiable();
      return;
    }

    const isActive: boolean = this.currentWeeklyPlanning.active;
    const popupText: string = isActive
      ? this._lblUpdateDeactivate
      : this._lblUpdateActivate;

    try {
      const allowChange: boolean =
        await this._alertService.presentAlertConfirmOrDismiss(
          this._lblUpdate,
          "",
          popupText
        );

      if (allowChange) {
        await this._weeklyPlanningService.ChangeActiveWeeklyPlanning(
          this.currentWeeklyPlanning.id,
          !isActive
        );
      }
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "onActivateDeactivateWeeklyPlanning()",
        "ERROR: " + error
      );
      await this._alertService.presentAlertSomethingWrong();
    }
  }

  /**
   * @returns the CSS class driving the thumbnail background for the current state
   */
  public getClassThumbnail(): string {
    if (!this.isOnline) {
      return "thumbnail-offline";
    }
    return this.currentWeeklyPlanning.active
      ? "thumbnail-active"
      : "thumbnail-online";
  }

  /**
   * @returns the CSS class driving the thumbnail icon colour for the current state
   */
  public getClassIcon(): string {
    if (!this.isOnline) {
      return "icon-offline";
    }
    return this.currentWeeklyPlanning.active ? "icon-active" : "icon-online";
  }

  /**
   * Subscribe to the network service to reflect the online/offline state in the thumbnail.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;
          this.isOnline = !(currentConnectionType === ConnectionType.NONE);
          this._cd.detectChanges();
        }
      );
  }
}
